import { useQuery } from '@tanstack/react-query';
import {
  type Core_StaffQuery,
  gqlClient,
  graphql,
  queryClient,
  type StaffFilter,
  type UseQueryReturnType,
} from '@tyro/api';
import { sortByDisplayName } from '@tyro/core/hooks';
import { useCallback } from 'react';
import { peopleKeys } from '../keys';

const staff = graphql(/* GraphQL */ `
  query core_staff($filter: StaffFilter) {
    core_staff(filter: $filter) {
      partyId
      externalSystemId
      person {
        partyId
        title {
          id
          name
          nameTextId
        }
        firstName
        lastName
        avatarUrl
        type
      }
      employmentCapacity {
        name
      }
      startDate
      endDate
      personalInformation {
        dateOfBirth
        primaryAddress {
          line1
          line2
          line3
          postCode
          county
          country
          city
        }
        gender
        primaryPhoneNumber {
          number
          areaCode
          countryCode
        }
        primaryEmail {
          email
        }
        emails {
          emailId
          email
          primaryEmail
        }
        ire {
          ppsNumber
        }
      }
      staffIre {
        teacherCouncilNumber
        staffPost {
          id
          name
        }
      }
      carRegistrationNumber
      parking
      position
      displayCode
      payrollNumber
      jobSharing
      availableForTeaching
      availableForSubstitution
      availableForSupportClasses
      competencySubjects {
        id
        name
        colour
      }
      qualifications
      peopleTags {
        id
        name
        colour
      }
    }
  }
`);

const staffInfoForSelect = graphql(/* GraphQL */ `
  query core_staffInfoForSelect($filter: StaffFilter) {
    core_staff(filter: $filter) {
      person {
        partyId
        title {
          nameTextId
          id
          name
        }
        firstName
        lastName
        avatarUrl
        type
      }
      externalSystemId
      displayCode
      personalInformation {
        ire {
          ppsNumber
        }
      }
    }
  }
`);

const staffQuery = (filter: StaffFilter) => ({
  queryKey: peopleKeys.staff.details(filter),
  queryFn: async () => gqlClient.request(staff, { filter }),
});

export function getStaff(filter: StaffFilter) {
  return queryClient.fetchQuery(staffQuery(filter));
}

export function useStaff(filter: StaffFilter) {
  return useQuery({
    ...staffQuery(filter),
    select: useCallback(({ core_staff }: Core_StaffQuery) => core_staff, []),
  });
}

const staffForSelectQuery = (filter: StaffFilter) => ({
  queryKey: peopleKeys.staff.forSelect(filter),
  queryFn: async () => {
    const { core_staff: staffData = [] } = await gqlClient.request(
      staffInfoForSelect,
      { filter },
    );

    return {
      core_staff: staffData
        .map(
          ({ person, displayCode, externalSystemId, personalInformation }) =>
            ({
              ...person,
              caption: displayCode ?? '-',
              externalSystemId,
              ppsNumber: personalInformation?.ire?.ppsNumber ?? null,
            }) as typeof person & {
              caption?: string;
              externalSystemId?: string | null;
              ppsNumber?: string | null;
            },
        )
        .sort(sortByDisplayName),
    };
  },
});

export function getStaffForSelect(filter: StaffFilter) {
  return queryClient.fetchQuery(staffForSelectQuery(filter));
}

export function useStaffForSelect(filter: StaffFilter, enabled = true) {
  return useQuery({
    ...staffForSelectQuery(filter),
    select: ({ core_staff }) => core_staff,
    enabled,
  });
}

export type ReturnTypeFromUseStaffData = UseQueryReturnType<
  typeof useStaff
>[number];

export type ReturnTypeFromUseStaffForSelect = UseQueryReturnType<
  typeof useStaffForSelect
>[number];
